import { useCallback, useState } from "react";
import {
  ArrowLeft, Headphones, MessageSquare, Send, UserCheck,
} from "lucide-react";
import { Badge, Button, Card, CardHeader, Input } from "@vseh/ui";
import { AgentBubble, UserBubble } from "@vseh/ai-components";

import {
  conversationsApi, customersApi, type ConversationDto, type MessageDto,
} from "../shared/api";
import { useFetch } from "../shared/hooks";
import { useT } from "../shared/i18n";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../shared/components/States";


export function ConversationsPage(): JSX.Element {
  const [openId, setOpenId] = useState<string | null>(null);
  if (openId) {
    return <ConversationDetail conversationId={openId} onBack={() => setOpenId(null)} />;
  }
  return <ConversationsList onOpen={setOpenId} />;
}


// ---------- list (synthesised from customers + their last conversation) ----
//
// We don't have a "list conversations" endpoint yet (Sprint 6 will add it).
// For Sprint 5, allow user to test the CRM pipeline by simulating an
// inbound message — that creates a conversation+customer they can
// then click into.
// --------------------------------------------------------------------------


function ConversationsList({ onOpen }: { onOpen: (id: string) => void }): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <SimulateInboundCard />
      <CustomersWithConversations onOpen={onOpen} />
    </div>
  );
}


function SimulateInboundCard(): JSX.Element {
  const t = useT();
  const [text, setText] = useState(() => t("conv.sample.text"));
  const [thread, setThread] = useState(`tg-thread-${Math.random().toString(36).slice(2, 8)}`);
  const [tg, setTg] = useState<number>(() =>
    Math.floor(1_000_000_000 + Math.random() * 9_000_000_000),
  );
  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState<{ correlation_id: string } | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const submit = async () => {
    if (!text.trim() || posting) return;
    setPosting(true);
    setError(null);
    try {
      const r = await conversationsApi.postInbound({
        channel: "telegram",
        channel_thread_id: thread,
        text,
        sender_label: "Demo Customer",
        telegram_user_id: tg,
      });
      setResult({ correlation_id: r.correlation_id });
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setPosting(false);
    }
  };

  const newSession = () => {
    setThread(`tg-thread-${Math.random().toString(36).slice(2, 8)}`);
    setTg(Math.floor(1_000_000_000 + Math.random() * 9_000_000_000));
    setResult(null);
  };

  return (
    <Card>
      <CardHeader
        title={t("conv.sim.title")}
        description={t("conv.sim.subtitle")}
        actions={<Button variant="ghost" size="xs" onClick={newSession}>{t("conv.sim.new_session")}</Button>}
      />
      <div className="space-y-2">
        <Input
          label={t("conv.sim.field.text")}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-2">
          <Input
            label={t("conv.sim.field.thread")}
            value={thread}
            onChange={(e) => setThread(e.target.value)}
          />
          <Input
            label={t("conv.sim.field.user")}
            value={String(tg)}
            onChange={(e) => setTg(Number(e.target.value) || 0)}
          />
        </div>
        {error && <ErrorBlock error={error} />}
        {result && (
          <div className="rounded-md border border-success-200 bg-success-50 px-3 py-2 text-xs text-success-800">
            {t("conv.sim.accepted", { cid: result.correlation_id })}
          </div>
        )}
        <Button
          variant="ai"
          leftIcon={<Send className="size-4" />}
          loading={posting}
          disabled={!text.trim()}
          onClick={submit}
        >
          {t("conv.sim.send")}
        </Button>
      </div>
    </Card>
  );
}


function CustomersWithConversations(_: { onOpen: (id: string) => void }): JSX.Element {
  const t = useT();
  // `onOpen` reserved for Sprint 6 when we have list-conversations-by-customer.
  const { data, error, loading, refetch } = useFetch(
    (signal) => customersApi.list(50, { signal }),
    [],
  );

  return (
    <Card>
      <CardHeader
        title={t("conv.list.title")}
        description={t("conv.list.subtitle")}
        actions={<Button variant="ghost" size="xs" onClick={refetch}>{t("common.refresh")}</Button>}
      />

      {loading && !data && <LoadingBlock />}
      {error && <ErrorBlock error={error} onRetry={refetch} />}
      {data && data.length === 0 && (
        <EmptyBlock
          title={t("conv.list.empty.title")}
          description={t("conv.list.empty.description")}
        />
      )}

      {data && data.length > 0 && (
        <ul className="divide-y divide-base-100">
          {data.map((c) => (
            <li key={c.id} className="py-2.5">
              <CustomerConversationLink
                customerId={c.id}
                displayName={c.display_name}
                telegramUserId={c.telegram_user_id}
              />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}


function CustomerConversationLink({
  customerId, displayName, telegramUserId,
}: {
  customerId: string;
  displayName: string;
  telegramUserId: number | null;
  // onOpen will be reused once "list conversations by customer"
  // endpoint lands in Sprint 6 — kept off the prop list to satisfy
  // strict noUnusedParameters until then.
}): JSX.Element {
  // We don't have a "find conversation by customer" endpoint yet
  // (Sprint 6 — will replace this best-effort link).
  // For now, show customer line and a hint.
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-base-900">{displayName}</div>
        <div className="mt-0.5 text-2xs text-base-500">
          {telegramUserId ? `tg user ${telegramUserId}` : "—"}
          <span className="ml-2 font-mono">customer:{customerId.slice(0, 8)}</span>
        </div>
      </div>
      <Badge tone="neutral">conv → Sprint 6</Badge>
    </div>
  );
}


// ---------- detail (when we have a real conversation_id) ----------------


function ConversationDetail({
  conversationId, onBack,
}: { conversationId: string; onBack: () => void }): JSX.Element {
  const t = useT();
  const meta = useFetch<ConversationDto>(
    (signal) => conversationsApi.get(conversationId, { signal }),
    [conversationId],
  );
  const messages = useFetch<MessageDto[]>(
    (signal) => conversationsApi.listMessages(conversationId, 200, { signal }),
    [conversationId],
  );

  const [actionInFlight, setActionInFlight] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);

  const takeOver = useCallback(async () => {
    setActionInFlight(true); setActionError(null);
    try {
      await conversationsApi.takeover(conversationId);
      meta.refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e : new Error(String(e)));
    } finally { setActionInFlight(false); }
  }, [conversationId, meta]);

  const release = useCallback(async () => {
    setActionInFlight(true); setActionError(null);
    try {
      await conversationsApi.release(conversationId);
      meta.refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e : new Error(String(e)));
    } finally { setActionInFlight(false); }
  }, [conversationId, meta]);

  const mode = meta.data?.agent_mode ?? "ai_lead";
  const isHumanOnly = mode === "human_only";

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="size-4" />} onClick={onBack}>
          {t("conv.back")}
        </Button>
      </div>

      {meta.error && <ErrorBlock error={meta.error} onRetry={meta.refetch} />}
      {meta.loading && !meta.data && <LoadingBlock />}
      {meta.data && (
        <Card density="compact">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 font-display text-lg font-semibold text-base-900">
                <MessageSquare className="size-4 text-ai-600" />
                {t("conv.title", { channel: meta.data.channel })}
              </div>
              <div className="mt-1 text-2xs text-base-500">
                thread: <code className="font-mono">{meta.data.channel_thread_id}</code>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge tone="ai" dot>{meta.data.status}</Badge>
              <Badge tone={isHumanOnly ? "danger" : "neutral"}>
                mode: {meta.data.agent_mode}
              </Badge>
              {isHumanOnly ? (
                <Button
                  size="xs" variant="ghost"
                  leftIcon={<UserCheck className="size-3.5" />}
                  loading={actionInFlight}
                  onClick={release}
                >
                  {t("conv.return_ai")}
                </Button>
              ) : (
                <Button
                  size="xs" variant="ghost"
                  leftIcon={<Headphones className="size-3.5" />}
                  loading={actionInFlight}
                  onClick={takeOver}
                >
                  {t("conv.take_over")}
                </Button>
              )}
            </div>
          </div>
          {actionError && (
            <div className="mt-2 rounded-sm border border-danger-200 bg-danger-50 px-3 py-1.5 text-xs text-danger-700">
              {actionError.message}
            </div>
          )}
        </Card>
      )}

      <Card>
        {messages.error && <ErrorBlock error={messages.error} onRetry={messages.refetch} />}
        {messages.loading && !messages.data && <LoadingBlock />}
        {messages.data && messages.data.length === 0 && (
          <EmptyBlock title={t("conv.thread.empty")} />
        )}
        {messages.data && messages.data.length > 0 && (
          <div className="space-y-3">
            {messages.data.map((m) =>
              m.role === "customer" ? (
                <UserBubble
                  key={m.id}
                  timestamp={new Date(m.created_at).toLocaleString("ru-RU")}
                >
                  {m.text}
                </UserBubble>
              ) : (
                <AgentBubble
                  key={m.id}
                  agentLabel={m.sender_label ?? m.role}
                  meta={{ model: "stub-v0", latencyMs: 0, costUsd: 0 }}
                >
                  {m.text}
                </AgentBubble>
              ),
            )}
          </div>
        )}

        {/* Operator reply composer — shown always; on AI-led threads it
            still sends but doesn't flip the mode. */}
        <OperatorComposer
          conversationId={conversationId}
          onSent={() => messages.refetch()}
        />
      </Card>
    </div>
  );
}


function OperatorComposer({
  conversationId, onSent,
}: { conversationId: string; onSent: () => void }): JSX.Element {
  const t = useT();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submit = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true); setError(null);
    try {
      await conversationsApi.reply(conversationId, t);
      setText("");
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally { setSending(false); }
  };

  return (
    <div className="mt-4 border-t border-base-200 pt-3">
      <div className="mb-1 text-2xs font-medium uppercase tracking-wide text-base-500">
        {t("conv.composer.label")}
      </div>
      <div className="flex gap-2">
        <Input
          className="flex-1"
          placeholder={t("conv.composer.placeholder")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        <Button
          variant="ai"
          leftIcon={<Send className="size-4" />}
          loading={sending}
          disabled={text.trim().length === 0}
          onClick={submit}
        >
          {t("conv.composer.send")}
        </Button>
      </div>
      {error && (
        <div className="mt-1 text-xs text-danger-700">{error.message}</div>
      )}
    </div>
  );
}
